import { GeographicDistributionItem } from "@/types/projects";

export interface LevelThreshold {
  max: number;
  label: string;
  color: [number, number, number, number];
  legendColor: string;
}

export const MAP_CONFIG = {
  center: [-117.1611, 32.7157] as [number, number],
  zoom: 9,
  basemap: "topo-vector",
} as const;

export const CIRCLE_CONFIG = {
  radiusMin: 500,
  radiusMax: 8000,
  points: 64,
  outlineWidth: 2,
} as const;

export const MAP_COLORS = {
  low: "#90EE90",
  mediumLow: "#22C55E",
  medium: "#FACC15",
  mediumHigh: "#F97316",
  high: "#EF4444",
} as const;

export const UI_CONFIG = {
  legendHeight: "24px",
  borderRadius: "8px",
} as const;

export const GEODESIC_CONSTANTS = {
  metersPerDegreeAtEquator: 111_320,
  metersPerDegreeLatitude: 110_540,
} as const;

/**
 * Helper function to convert hex color to RGBA array
 */
const hexToRgba = (
  hex: string,
  alpha: number
): [number, number, number, number] => {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return [r, g, b, alpha];
};

export const LEVEL_THRESHOLDS: LevelThreshold[] = [
  {
    max: 29,
    label: "Low Level",
    color: hexToRgba(MAP_COLORS.low, 0.7),
    legendColor: MAP_COLORS.low,
  },
  {
    max: 59,
    label: "Medium-Low Level",
    color: hexToRgba(MAP_COLORS.mediumLow, 0.7),
    legendColor: MAP_COLORS.mediumLow,
  },
  {
    max: 89,
    label: "Medium Level",
    color: hexToRgba(MAP_COLORS.medium, 0.7),
    legendColor: MAP_COLORS.medium,
  },
  {
    max: 100,
    label: "Medium-High Level",
    color: hexToRgba(MAP_COLORS.mediumHigh, 0.7),
    legendColor: MAP_COLORS.mediumHigh,
  },
  {
    max: Infinity,
    label: "High Level",
    color: hexToRgba(MAP_COLORS.high, 0.7),
    legendColor: MAP_COLORS.high,
  },
];

/**
 * Extracts coordinates from a geographic distribution item
 * @param item - Geographic distribution data item
 * @returns Tuple of [latitude, longitude]
 */
export const getCityCoordinates = (
  item: GeographicDistributionItem
): [number, number] => {
  return [item.latitude, item.longitude];
};

/**
 * Determines the project level threshold based on project count
 * @param count - Number of projects
 * @returns Level threshold configuration including color and label
 */
export const getProjectLevel = (count: number): LevelThreshold => {
  const level = LEVEL_THRESHOLDS.find((threshold) => count <= threshold.max);
  return level || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
};

/**
 * Creates polygon points for a circle at given coordinates
 * Uses geodesic calculations to account for Earth's curvature
 * @param centerLon - Center longitude
 * @param centerLat - Center latitude
 * @param radiusMeters - Radius in meters
 * @returns Array of polygon rings (coordinates)
 */
export const createCirclePoints = (
  centerLon: number,
  centerLat: number,
  radiusMeters: number
): number[][][] => {
  const points: number[][] = [];

  const radiusLon =
    radiusMeters /
    GEODESIC_CONSTANTS.metersPerDegreeAtEquator /
    Math.cos((centerLat * Math.PI) / 180);
  const radiusLat = radiusMeters / GEODESIC_CONSTANTS.metersPerDegreeLatitude;

  for (let i = 0; i <= CIRCLE_CONFIG.points; i++) {
    const angle = (i / CIRCLE_CONFIG.points) * 2 * Math.PI;
    const lon = centerLon + radiusLon * Math.cos(angle);
    const lat = centerLat + radiusLat * Math.sin(angle);
    points.push([lon, lat]);
  }

  return [points];
};

/**
 * Calculates circle radius based on project count relative to maximum
 * Uses linear scaling between min and max radius values
 * @param projectCount - Number of projects at this location
 * @param maxProjects - Maximum project count across all locations
 * @returns Radius in meters
 */
export const calculateCircleRadius = (
  projectCount: number,
  maxProjects: number
): number => {
  const { radiusMin, radiusMax } = CIRCLE_CONFIG;

  if (maxProjects === 0) return radiusMin;

  return radiusMin + (projectCount / maxProjects) * (radiusMax - radiusMin);
};
